"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { Pagination } from "@/components/ui/pagination";

export interface UploadAreaContextValue {
  openFilePicker: () => void;
  isDragOver: boolean;
  disabled: boolean;
  handleDragEnter: (e: DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  handleDragOver: (e: DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: DragEvent<HTMLDivElement>) => void;
}

const UploadAreaContext = createContext<UploadAreaContextValue | null>(null);

export function useUploadArea(): UploadAreaContextValue {
  const ctx = useContext(UploadAreaContext);
  if (!ctx) {
    throw new Error("useUploadArea must be used within UploadArea.Provider");
  }
  return ctx;
}

export interface UploadAreaProviderProps {
  accept: string;
  disabled?: boolean;
  multiple?: boolean;
  onSelect: (files: File[]) => void;
  children: ReactNode;
}

function UploadAreaProvider({
  accept,
  disabled = false,
  multiple = false,
  onSelect,
  children,
}: UploadAreaProviderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const openFilePicker = useCallback(() => {
    if (!disabled) fileRef.current?.click();
  }, [disabled]);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounter.current = 0;
      if (disabled) return;
      const list = e.dataTransfer.files;
      if (!list?.length) return;
      const arr = multiple ? Array.from(list) : [list[0]];
      onSelect(arr);
    },
    [disabled, multiple, onSelect],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (list?.length) {
        onSelect(Array.from(list));
      }
      e.target.value = "";
    },
    [onSelect],
  );

  const ctxValue = useMemo<UploadAreaContextValue>(
    () => ({
      openFilePicker,
      isDragOver,
      disabled,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    }),
    [
      openFilePicker,
      isDragOver,
      disabled,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    ],
  );

  return (
    <UploadAreaContext.Provider value={ctxValue}>
      {children}
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileChange}
      />
    </UploadAreaContext.Provider>
  );
}

interface UploadAreaContainerProps {
  className?: string;
  children: ReactNode;
}

function UploadAreaContainer({
  className,
  children,
}: UploadAreaContainerProps) {
  const {
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  } = useUploadArea();

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        "w-full rounded-lg border border-dashed px-6 py-8 flex flex-col items-center gap-4 shrink-0 transition-colors",
        isDragOver
          ? "border-primary bg-primary/6"
          : "border-contrast/20 bg-contrast/[0.02]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function UploadAreaIcon({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const { isDragOver } = useUploadArea();
  return (
    <div
      className={cn(
        "transition-opacity",
        isDragOver ? "opacity-20" : "opacity-40",
        className,
      )}
    >
      {children}
    </div>
  );
}

type UploadAreaActionProps = Omit<ButtonProps, "onClick" | "type">;

function UploadAreaActions({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-3 max-w-full",
        className,
      )}
    >
      {children}
    </div>
  );
}

function UploadAreaAction({
  disabled: disabledProp,
  ...props
}: UploadAreaActionProps) {
  const { openFilePicker, disabled: ctxDisabled } = useUploadArea();
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={ctxDisabled || disabledProp}
      onClick={openFilePicker}
      {...props}
    />
  );
}

function UploadAreaContent({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "text-sm text-contrast/50 text-center max-w-full break-words",
        className,
      )}
    >
      {children}
    </div>
  );
}

function UploadAreaLimits({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "text-xs text-contrast/30 border border-contrast/10 rounded px-2 py-0.5",
        className,
      )}
    >
      {children}
    </span>
  );
}

function UploadAreaDropping({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <p className={cn("text-sm text-primary font-medium", className)}>
      {children}
    </p>
  );
}

function UploadAreaResultsContainer({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-3 min-h-0 flex-1", className)}>
      {children}
    </div>
  );
}

function UploadAreaResultsToolbar({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 shrink-0 min-w-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

function UploadAreaResults({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "border border-border rounded-lg overflow-x-hidden overflow-y-auto flex-1 min-h-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface UploadAreaResultsPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

function UploadAreaResultsPagination({
  page,
  totalPages,
  onPageChange,
  className,
}: UploadAreaResultsPaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <Pagination.Root
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
      variant="upload"
    >
      <Pagination.Row className={cn("justify-center shrink-0", className)}>
        <Pagination.Prev mode="icon" />
        <Pagination.Pages strategy="uploadLeading" maxVisible={5} />
        <Pagination.Next mode="icon" />
      </Pagination.Row>
    </Pagination.Root>
  );
}

function UploadAreaResultsRowRoot({
  matched,
  className,
  children,
}: {
  matched: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 min-h-10 min-w-0 px-2 border-b border-border",
        matched ? "bg-surface-elevated" : "bg-[rgba(233,115,117,0.08)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function UploadAreaResultsRowStart({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex items-center gap-1.5 min-w-0 flex-1", className)}>
      {children}
    </div>
  );
}

function UploadAreaResultsRowMain({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex items-center gap-1.5 min-w-0 flex-1", className)}>
      {children}
    </div>
  );
}

function UploadAreaResultsRowEnd({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={cn("shrink-0", className)}>{children}</div>;
}

function UploadAreaResultsRowSelect({
  selected,
  onToggle,
}: {
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-center size-6 shrink-0 cursor-pointer"
    >
      <div
        className={cn(
          "size-4 rounded-lg flex items-center justify-center",
          selected ? "bg-primary" : "border border-contrast/40",
        )}
      >
        {selected && (
          <Icon name="IconCheckmark1" size={12} className="text-foreground" />
        )}
      </div>
    </button>
  );
}

function UploadAreaResultsRowSelectSpacer() {
  return <div className="size-6 shrink-0" />;
}

function UploadAreaResultsRowAvatar({
  src,
  alt = "",
}: {
  src?: string | null;
  alt?: string;
}) {
  return (
    <Avatar className="size-5 shrink-0 rounded-lg border border-border">
      <AvatarImage src={src} alt={alt} />
      <AvatarFallback className="rounded-lg bg-contrast/5">
        <span className="sr-only">{alt || "Avatar"}</span>
      </AvatarFallback>
    </Avatar>
  );
}

function UploadAreaResultsRowLabel({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "text-sm font-medium text-heading truncate min-w-0",
        className,
      )}
    >
      {children}
    </span>
  );
}

function UploadAreaResultsRowBadge({
  variant,
  children,
}: {
  variant: "success" | "danger";
  children: ReactNode;
}) {
  if (variant === "success") {
    return (
      <div className="rounded-sm border border-[rgba(99,246,143,0.1)] bg-[rgba(99,246,143,0.05)] overflow-clip p-0.5">
        <div className="flex items-center px-1">
          <span className="text-xs font-medium leading-4 whitespace-nowrap text-[#63f68f]">
            {children}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-sm border border-border bg-[rgba(233,115,117,0.1)] overflow-clip p-0.5">
      <div className="flex items-center px-1">
        <span className="text-xs font-medium leading-4 whitespace-nowrap text-[#e97375]">
          {children}
        </span>
      </div>
    </div>
  );
}

const UploadAreaResultsRow = Object.assign(UploadAreaResultsRowRoot, {
  Start: UploadAreaResultsRowStart,
  Main: UploadAreaResultsRowMain,
  End: UploadAreaResultsRowEnd,
  Select: UploadAreaResultsRowSelect,
  SelectSpacer: UploadAreaResultsRowSelectSpacer,
  Avatar: UploadAreaResultsRowAvatar,
  Label: UploadAreaResultsRowLabel,
  Badge: UploadAreaResultsRowBadge,
});

export const UploadArea = Object.assign(UploadAreaProvider, {
  Provider: UploadAreaProvider,
  Container: UploadAreaContainer,
  Icon: UploadAreaIcon,
  Actions: UploadAreaActions,
  Action: UploadAreaAction,
  Content: UploadAreaContent,
  Limits: UploadAreaLimits,
  Dropping: UploadAreaDropping,
  ResultsContainer: UploadAreaResultsContainer,
  ResultsToolbar: UploadAreaResultsToolbar,
  Results: UploadAreaResults,
  ResultsPagination: UploadAreaResultsPagination,
  ResultsRow: UploadAreaResultsRow,
});
